import { reportError } from 'src/utils/reporting/report-errors'

interface IUTMParams {
  source: string;
  medium: string;
}

export const withUTMParams = (url: string, { source, medium }: IUTMParams) =>
  `${url}?utm_source=${source}&utm_medium=${medium}&utm_campaign=share`

export const toAbsoluteUrl = (path: string) => window.location.origin + path

export const canShareNatively = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function'

const errorName = (error: unknown) =>
  typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined

// An InvalidStateError less than this after the call that is still open counts as a double
// activation and stays quiet; an open call older than this is taken as stale and the popover
// takes over.
const DOUBLE_ACTIVATION_MS = 1000

let openCallStartedAt: number | undefined

interface INativeShareCallbacks {
  onShared: () => void;
  onFallback: () => void;
}

export const shareNatively = (data: ShareData, { onShared, onFallback }: INativeShareCallbacks) => {
  if (!canShareNatively()) {
    onFallback()
    return Promise.resolve()
  }

  const startedAt = Date.now()
  const earlierStartedAt = openCallStartedAt
  if (earlierStartedAt === undefined) {
    openCallStartedAt = startedAt
  }
  const settle = () => {
    if (openCallStartedAt === startedAt) {
      openCallStartedAt = undefined
    }
  }

  return navigator.share(data)
    .then(() => {
      settle()
      onShared()
    })
    .catch((error: unknown) => {
      settle()
      const name = errorName(error)
      if (name === 'AbortError') {
        return
      }
      if (name === 'InvalidStateError') {
        if (earlierStartedAt === undefined || startedAt - earlierStartedAt >= DOUBLE_ACTIVATION_MS) {
          onFallback()
        }
        return
      }
      reportError('native share failed', { error })
      onFallback()
    })
}
